// AssignmentBoardPage.tsx  (PROTOTYPE)
//
// The "raid sheet" rebuild: a boss x duty-lane board that prefills itself from
// the planner roster instead of being typed out by hand.
//
// Density is the whole design. A raider checks this between pulls, so a boss
// has to fit on one screen with no scrolling and read in about two seconds.
// That means one line per duty (cue + name, never a sentence), class colour as
// the only decoration, and no card chrome around anything that is not a boss.
// Cards, gradients and big type all got cut for exactly this reason: they push
// a 17-duty boss over three screens and make the sheet feel like homework.
//
// Decisions autosave to raid_sheet; the board itself is never stored because it
// is derived from the roster plus the encounter templates plus those decisions.
// Depth charts go to guild_sheet_defaults instead, since "our best warlock" is
// a property of the guild rather than of one Wednesday.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RaidMarkerIcon, { RAID_MARKERS } from "../RaidMarkerIcon";
import { getWowClassColor as classColor } from "../wowClassColor";
import { Floating, Tip } from "../Floating";
import {
  BossAssignment, BossPlan, DutyLane, DutySlot, LANE_ACCENT, OverrideMap,
  DisabledMap, Raider, ROLE_META, ROLE_ORDER, RoleKey, SlotSource, buildBoard,
  RosterSnapshot, SlotTarget, clashesFor, eligibleFor, laneId, loadByRaider,
  normalizeRole, canonClass, slotId, slotMarkers, snapshotOf, targetId,
} from "./assignmentModel";
import {
  DepthOrder, NightGroup, NightRole, ResolvedRole, RoleAssignments,
  RoleTargets, resolveDepth, resolveNightRoles, rolesInGroup,
} from "./nightRoles";
import { EMPTY_CONFIG, SheetConfig, classOrder, withTraits } from "./gameData";
import BlessingBoard from "./BlessingBoard";
import {
  BlessingKey, BlessingPlan, BlessingState, blessingCatalogue,
  blessingDutiesFor, cellKey, colorOf, planBlessings, shortOf, singleKey,
} from "./blessings";

import { tc, useLegible, optionStyle } from "../theme/legible";
import { DISPLAY } from "../alignmentUi";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-4)]";
const FIELD = "rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1 text-xs text-[var(--c-ink)] outline-none focus:border-amber-400/50";

// A native <option> popup is drawn by the OS, not the page: on Windows it is
// white. The select carries the player's class colour, and Priest white is
// #FFFFFF, so the list came out white on white. Every option therefore states
// its own background rather than inheriting one we do not control.
const optStyle = (color?: string | null) =>
  color ? { ...optionStyle(), color } : optionStyle();

const SOURCE_HINT: Record<SlotSource, (label: string) => string> = {
  auto: (l) => `${l} (auto-filled from the roster)`,
  night: (l) => `${l} (set on the night sheet, same on every boss)`,
  override: (l) => `${l} (overridden on this boss only)`,
};

// ── Slot row ─────────────────────────────────────────────────
// One line: cue, then name. The whole line is the swap control, which is why
// there is no separate button or icon competing for attention.

function SlotRow({
  id, slot, raider, eligible, source, doubled, isMe, targetLabel, replacedName,
  targetChoices, canManage, onPick, onPickTarget, onHide,
}: {
  id: string;
  slot: DutySlot;
  raider: Raider | null;
  eligible: Raider[];
  source: SlotSource;
  doubled: boolean;
  isMe: boolean;
  targetLabel?: string | null;
  replacedName?: string | null;
  targetChoices?: TargetChoices | null;
  canManage: boolean;
  onPick: (characterId: string) => void;
  onPickTarget: (value: string) => void;
  onHide: () => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const marks = slotMarkers(slot);

  // The menu lives in document.body now, so "clicked outside" has to check the
  // menu as well as the row: they are no longer in the same subtree.
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (boxRef.current && boxRef.current.contains(t)) return;
      if (menuRef.current && menuRef.current.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={boxRef} data-testid={`slot-${id}`}
         data-raider={raider ? raider.name : ""} className="relative">
      {/* Cue is right-aligned against a hairline so every name in a lane starts
          on the same x. That single edge is what makes a column scannable. */}
      {/* A raider gets the same row without the affordance: no pointer, no
          hover lift, no picker. Read-only has to look read-only, not like a
          control that silently does nothing. */}
      {React.createElement(
        canManage ? "button" : "div",
        {
        onClick: canManage ? () => setOpen((v) => !v) : undefined,
        // Your own row is outlined, not filled. A block of amber on every row
        // you hold turns the sheet into a highlighter mess; a hairline ring
        // plus a brighter cue reads instantly and leaves the colour of the
        // board alone.
        className: `group flex w-full items-center gap-1.5 rounded px-1.5 py-[3px] text-left
                    transition-colors odd:bg-[var(--c-bg-veil)]
                    ${canManage ? "cursor-pointer hover:bg-[var(--c-sunken)]" : ""}
                    ${isMe ? "ring-1 ring-inset ring-amber-400/70" : ""}`,
        },
        <>
        {marks.length > 0
          ? <span className="flex shrink-0 items-center gap-[2px]">
              {marks.map((m) => <RaidMarkerIcon key={m} marker={m} size={11} />)}
            </span>
          : <span className="w-[11px] shrink-0" />}
        <span className={`w-[42px] shrink-0 truncate text-right text-[10px] uppercase tracking-wide
                          ${isMe ? "font-semibold text-amber-700 dark:text-amber-400"
                                 : "text-[var(--c-ink-4)] group-hover:text-[var(--c-ink-3)]"}`}>
          {slot.short}
        </span>
        <span className={`h-3 w-px shrink-0 ${isMe ? "bg-amber-400/40" : "bg-[var(--c-sunken)]"}`} />
        {raider ? (
          <span className={`truncate text-[12.5px] leading-tight
                            ${isMe ? "font-bold" : "font-semibold"}`}
                style={{ color: tc(classColor(raider.className)) }}>
            {raider.name}
          </span>
        ) : (
          <span className="text-[12.5px] text-[var(--c-ink-4)]">&mdash;</span>
        )}
        {/* The second field: "onto Aelisi", "covers Group 5". A duty with a
            target is meaningless without it, so it rides the same line. */}
        {targetLabel && (
          <span className="ml-1 truncate text-[10.5px] text-[var(--c-ink-3)]">{targetLabel}</span>
        )}
        {replacedName && (
          <Tip tip={<><b>Stand-in.</b> This was {replacedName} last time, who is not
                     in tonight's raid. Closest match picked. Confirm it.</>}
               className="ml-auto shrink-0 rounded bg-violet-400/20 px-1 text-[9px]
                          font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            New
          </Tip>
        )}
        {doubled && !replacedName && (
          <Tip tip={<><b>Doubled up.</b> {raider ? raider.name : "This player"} already
                     has another duty on this boss.</>}
               className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400
                          shadow-[0_0_5px_rgba(245,158,11,0.8)]">
            {""}
          </Tip>
        )}
        {source !== "auto" && !doubled && !replacedName && (
          <Tip tip={source === "override"
                      ? <>Overridden on this boss only. The night sheet is unchanged.</>
                      : <>Set on the night sheet, so it is the same on every boss.</>}
               className={`ml-auto h-1.5 w-1.5 shrink-0 rounded-full
                           ${source === "override" ? "bg-sky-400" : "bg-[var(--c-ink-4)]"}`}>
            {""}
          </Tip>
        )}
        </>
      )}

      {/* Portalled: lane panels clip their children, so an absolutely
          positioned menu was cut off at the card edge whatever its z-index. */}
      <Floating anchor={boxRef.current} open={open && canManage}>
        <div ref={menuRef}
             className="max-h-56 w-44 overflow-auto rounded-lg border
                        border-[var(--c-line)] bg-[var(--c-sunken)] p-1 shadow-2xl shadow-black/80
                        ring-1 ring-[var(--c-line)]">
          <button
            onClick={() => { onPick(""); setOpen(false); }}
            className="w-full rounded px-2 py-1 text-left text-[11px] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)]"
          >
            Clear slot
          </button>
          <button
            onClick={() => { onHide(); setOpen(false); }}
            className="w-full rounded px-2 py-1 text-left text-[11px] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)]"
          >
            Not needed here
          </button>
          <div className="my-0.5 h-px bg-[var(--c-sunken)]" />
          {eligible.length === 0 && (
            <div className="px-2 py-1.5 text-[11px] text-[var(--c-ink-4)]">Nobody can cover this.</div>
          )}
          {eligible.map((r) => (
            <button
              key={r.characterId}
              onClick={() => { onPick(r.characterId); setOpen(false); }}
              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-[var(--c-sunken)]"
            >
              <span className="text-[12px] font-semibold" style={{ color: tc(classColor(r.className)) }}>
                {r.name}
              </span>
              <span className="ml-auto text-[10px] text-[var(--c-ink-4)]">{ROLE_META[r.role].short}</span>
            </button>
          ))}

          {/* Who the duty lands ON. A misdirection is meaningless without it,
              and the right answer changes per boss: platform 1 on Al'ar, the
              Vashj tank on Vashj. So it is set here, not only on the night
              sheet. */}
          {targetChoices && (
            <>
              <div className="mt-1 border-t border-[var(--c-line)] px-2 pb-0.5 pt-1.5
                              text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--c-ink-4)]">
                {targetChoices.label}
              </div>
              {targetChoices.kind === "raider"
                ? (targetChoices.raiders || []).map((r) => (
                    <button
                      key={r.characterId}
                      onClick={() => { onPickTarget(r.characterId); setOpen(false); }}
                      className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-[var(--c-sunken)]"
                    >
                      <span className="text-[12px] font-semibold"
                            style={{ color: tc(classColor(r.className)) }}>
                        {r.name}
                      </span>
                      <span className="ml-auto text-[10px] text-[var(--c-ink-4)]">
                        {ROLE_META[r.role].short}
                      </span>
                    </button>
                  ))
                : (targetChoices.options || []).map((o) => (
                    <button
                      key={o || "_none"}
                      onClick={() => { onPickTarget(o); setOpen(false); }}
                      className="w-full rounded px-2 py-1 text-left text-[12px]
                                 text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)]"
                    >
                      {o || "Not set"}
                    </button>
                  ))}
            </>
          )}
        </div>
      </Floating>
    </div>
  );
}

// What a row's second field may be set to, resolved by the caller because it
// needs the roster.
type TargetChoices = {
  kind: "raider" | "text";
  label: string;
  raiders?: Raider[];
  options?: string[];
};

type VisibleGroup = { label: string | null; bosses: BossAssignment[] };

// ── Custom boxes ─────────────────────────────────────────────
// The templates cover the fights we know. A lead always has one more thing:
// a kite rotation this week, a door duty, a "who calls the pull". A custom box
// is the same shape as any other lane (header, rows of marker + cue + player)
// but every field is theirs to type, and it belongs to one section: a boss,
// the trash sheet, or General.
export type CustomRow = { key: string; label: string; markers: number[] };

// A box declares whether its rows carry a second field, the way Misdirections
// carry "onto whom" and Decurses carry "covers what". Most boxes are just a
// duty and a person, so it is off by default.
export type CustomTargetKind = "none" | "player" | "text";
export type CustomLane = {
  key: string;
  label: string;
  target: CustomTargetKind;
  targetLabel: string;
  rows: CustomRow[];
};
export type CustomLanes = Record<string, CustomLane[]>;   // sectionKey -> boxes

let customSeq = 0;
const nextKey = (p: string) => `${p}${Date.now().toString(36)}${customSeq++}`;

export const newCustomLane = (): CustomLane => ({
  key: nextKey("cl_"),
  label: "New box",
  target: "none",
  targetLabel: "Onto",
  rows: [{ key: nextKey("cr_"), label: "Row 1", markers: [] }],
});

// Free text for a text-target box: a lead types their own options, comma
// separated, rather than being handed ours.
export const CUSTOM_TEXT_OPTIONS = ["", "Group 1", "Group 2", "Group 3",
  "Group 4", "Group 5", "Melee", "Ranged", "Tank", "Raid", "Left", "Right"];

// Rendered as real lanes so they pick up auto-fill, overrides, filtering and
// the "not needed here" trim for free. An empty rule means anyone may fill it.
export function customToLanes(boxes: CustomLane[] | undefined): DutyLane[] {
  return (boxes || []).map((b) => {
    const target: SlotTarget | undefined =
      b.target === "player"
        ? { kind: "raider", label: b.targetLabel || "Onto", rule: {} }
      : b.target === "text"
        ? { kind: "text", label: b.targetLabel || "Covers", options: CUSTOM_TEXT_OPTIONS }
      : undefined;
    return {
      key: b.key,
      label: b.label || "Untitled",
      accent: "control" as const,
      slots: b.rows.map((r) => ({
        key: r.key,
        label: r.label || "Row",
        short: r.label || "Row",
        markers: r.markers,
        rule: {},
        target,
      })),
    };
  });
}

function targetChoicesFor(slot: DutySlot, roster: Raider[]): TargetChoices | null {
  const t: SlotTarget | undefined = slot.target;
  if (!t) return null;
  if (t.kind === "raider") {
    return { kind: "raider", label: t.label, raiders: eligibleFor(roster, t.rule) };
  }
  return { kind: "text", label: t.label, options: t.options };
}

// "onto Aelisi" for a Misdirection, "covers Melee" for a Decurse. Rendered
// dim and inline: it qualifies the duty rather than competing with it.
function targetLabelFor(s: {
  slot: DutySlot; target: string | null; targetRaider: Raider | null;
}): string | null {
  if (!s.slot.target || !s.target) return null;
  if (s.slot.target.kind === "raider") {
    return s.targetRaider ? `${s.slot.target.label.toLowerCase()} ${s.targetRaider.name}` : null;
  }
  return s.target;
}

// The editor for one custom box. Deliberately plain inputs: this is the only
// place on the board where a lead is authoring rather than assigning.
function CustomEditor({
  box, onChange, onRemove, onDone,
}: {
  box: CustomLane;
  onChange: (next: CustomLane) => void;
  onRemove: () => void;
  onDone: () => void;
}) {
  const setRow = (key: string, patch: Partial<CustomRow>) =>
    onChange({ ...box, rows: box.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) });

  return (
    <div data-testid={`custom-edit-${box.key}`}
         className="rounded-lg border border-amber-400/25 bg-[var(--c-surface)] p-2">
      <div className="mb-1.5 flex items-center gap-1.5">
        <input
          value={box.label}
          onChange={(e) => onChange({ ...box, label: e.target.value })}
          placeholder="Box name"
          aria-label="Box name"
          className="min-w-0 flex-1 rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1.5 py-[3px]
                     text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300
                     outline-none focus:border-amber-400/60"
        />
        {/* Without this there is no way out of the editor: the finished lane
            is hidden while its box is being edited. */}
        <button onClick={onDone}
                className="rounded bg-[var(--c-accent)] px-1.5 py-[2px] text-[10px] font-bold
                           uppercase tracking-wider text-[var(--c-ink-on-fill)] hover:bg-[var(--c-accent-hover)]">
          Done
        </button>
        <Tip tip="Delete this box"><button onClick={onRemove}
                className="px-1 text-[11px] text-[var(--c-ink-4)] hover:text-red-700 dark:hover:text-red-300">✕</button></Tip>
      </div>

      {/* Does this box carry a second field? Misdirections do ("onto whom"),
          decurses do ("covers what"), a kill order does not. */}
      <div className="mb-1.5 flex items-center gap-1">
        <span className="text-[9px] uppercase tracking-wider text-[var(--c-ink-4)]">Second field</span>
        <select
          value={box.target}
          onChange={(e) => onChange({ ...box, target: e.target.value as CustomTargetKind })}
          aria-label="Second field"
          className="rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1 py-[2px]
                     text-[10px] text-[var(--c-ink-2)] outline-none focus:border-amber-400/60"
        >
          <option value="none" style={optionStyle()}>None</option>
          <option value="player" style={optionStyle()}>A player</option>
          <option value="text" style={optionStyle()}>A label</option>
        </select>
        {box.target !== "none" && (
          <input
            value={box.targetLabel}
            onChange={(e) => onChange({ ...box, targetLabel: e.target.value })}
            placeholder="Onto"
            aria-label="Second field name"
            className="w-[70px] rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1 py-[2px]
                       text-[10px] outline-none focus:border-amber-400/60"
          />
        )}
      </div>

      {box.rows.map((r) => (
        <div key={r.key} className="mb-1.5">
          <div className="flex items-center gap-1">
            <input
              value={r.label}
              onChange={(e) => setRow(r.key, { label: e.target.value })}
              placeholder="Duty"
              aria-label="Row label"
              className="min-w-0 flex-1 rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1.5 py-[3px]
                         text-[11px] outline-none focus:border-amber-400/60"
            />
            <Tip tip="Remove this row"><button
              onClick={() => onChange({ ...box, rows: box.rows.filter((x) => x.key !== r.key) })}
              className="px-1 text-[11px] text-[var(--c-ink-4)] hover:text-red-700 dark:hover:text-red-300"
            >
              −
            </button></Tip>
          </div>
          {/* Toggles, not a dropdown: a row often holds two or three marks
              and picking one at a time meant duplicating the person. */}
          <div className="mt-0.5 flex flex-wrap items-center gap-0.5 pl-0.5">
            {RAID_MARKERS.map((m) => {
              const on = r.markers.indexOf(m.i) >= 0;
              return (
                <button
                  key={m.i}
                  aria-label={m.name}
                  aria-pressed={on}
                  onClick={() => setRow(r.key, {
                    markers: on ? r.markers.filter((x) => x !== m.i)
                                : r.markers.concat([m.i]),
                  })}
                  className={`rounded p-[2px] transition-opacity
                              ${on ? "bg-[var(--c-sunken-2)] opacity-100" : "opacity-25 hover:opacity-70"}`}
                >
                  <RaidMarkerIcon marker={m.i} size={12} />
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <button
        onClick={() => onChange({
          ...box,
          rows: box.rows.concat([{
            key: nextKey("cr_"), label: `Row ${box.rows.length + 1}`, markers: [],
          }]),
        })}
        className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider
                   text-[var(--c-ink-4)] hover:text-amber-700 dark:hover:text-amber-300"
      >
        + Row
      </button>
    </div>
  );
}

// ── Night setup ──────────────────────────────────────────────
// The equivalent of the guild's setup tab: one dropdown per repeating job,
// set once, carried into every boss. Depth-chart roles show where they came
// from rather than offering a redundant second place to reorder warlocks.

function RolePicker({
  value, roster, placeholder, onPick, tint,
}: {
  value: Raider | null;
  roster: Raider[];
  placeholder: string;
  onPick: (id: string) => void;
  tint?: boolean;
}) {
  return (
    <select
      value={value ? value.characterId : ""}
      onChange={(e) => onPick(e.target.value)}
      className={`w-full truncate rounded border px-1.5 py-[3px] text-[12px] font-semibold outline-none
                  ${tint ? "border-amber-400/40 bg-amber-400/[0.06]" : "border-[var(--c-line)] bg-[var(--c-surface)]"}
                  focus:border-amber-400/60`}
      style={{ color: tc(value ? classColor(value.className) : undefined),
               backgroundColor: tint ? undefined : "#0D1014" }}
    >
      <option value="" style={optionStyle()}>{placeholder}</option>
      {roster.map((r) => (
        <option key={r.characterId} value={r.characterId}
                style={optStyle(classColor(r.className))}>{r.name}</option>
      ))}
    </select>
  );
}

// The blessing grid, reduced to the one person reading it. A paladin sees what
// they cast and on whom; everybody else sees what they should be carrying, so
// they can tell at a glance that they are missing a buff.
function MyBlessings({ plan, characterId }: {
  plan: BlessingPlan; characterId: string;
}) {
  const duties = blessingDutiesFor(plan, characterId);
  if (duties.length === 0) return null;
  const cast = duties.filter((d) => d.kind === "cast");
  const get = duties.filter((d) => d.kind === "receive");

  const line = (label: string, rows: typeof duties, showDetail: boolean) =>
    rows.length === 0 ? null : (
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-[2px]">
        <span className={`${LABEL} w-[52px] shrink-0`}>{label}</span>
        {rows.map((d, i) => (
          <span key={`${d.blessing}-${i}`} className="flex items-baseline gap-1">
            <span className="text-[12px] font-semibold"
                  style={{ color: tc(colorOf(plan.catalogue, d.blessing)) }}>
              {shortOf(plan.catalogue, d.blessing)}
            </span>
            {showDetail && (
              <span className="text-[10px] text-[var(--c-ink-4)]">{d.detail}</span>
            )}
            {d.rebuff && (
              <span className="text-[9px] font-bold text-amber-700/70 dark:text-amber-300/70" title="10 minutes, recast it">
                10m
              </span>
            )}
          </span>
        ))}
      </div>
    );

  return (
    <div data-testid="my-blessings"
         className="mb-3 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-1.5">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em]"
           style={{ color: tc("#c084fc") }}>
        Blessings
      </div>
      {line("Cast", cast, true)}
      {line("You get", get, false)}
    </div>
  );
}

function NightSetup({
  canManage, roster, groups, roles, resolved, assignments, targets, depth,
  depthOrder, onRole, onTarget, onReorder, onReset,
}: {
  canManage: boolean;
  roster: Raider[];
  groups: NightGroup[];
  roles: NightRole[];
  resolved: Record<string, ResolvedRole>;
  assignments: RoleAssignments;
  targets: RoleTargets;
  depth: DepthOrder;
  depthOrder: DepthOrder;
  onRole: (roleKey: string, characterId: string) => void;
  onTarget: (roleKey: string, value: string) => void;
  onReorder: (className: string, from: number, to: number) => void;
  onReset: () => void;
}) {
  const byId: Record<string, Raider> = {};
  roster.forEach((r) => { byId[r.characterId] = r; });
  const eligibleFor = (role: NightRole) =>
    roster.filter((r) => {
      if (r.isBench) return false;
      if (role.rule.roles && role.rule.roles.indexOf(r.role) < 0) return false;
      if (role.rule.classes && role.rule.classes.indexOf(canonClass(r.className)) < 0) return false;
      return true;
    });

  // Only charts worth ordering: a lone warlock has no first and second.
  const charts = Object.keys(depth).filter((c) => (depth[c] || []).length > 1);
  const touched = Object.keys(assignments).length + Object.keys(targets).length
    + Object.keys(depthOrder).length;

  return (
    <div data-testid="night-setup"
         className="mb-5 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-3">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700/80 dark:text-amber-400/80">
          Night sheet
        </span>
        <span className="text-[11px] text-[var(--c-ink-4)]">
          {canManage
            ? "Set once, applies to every boss. Override a single boss on the board below."
            : "Set by your officers. Applies to every boss."}
        </span>
        {canManage && touched > 0 && (
          <button onClick={onReset}
                  className="ml-auto text-[11px] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]">
            Reset to auto
          </button>
        )}
      </div>

      {charts.length > 0 && (
        <div className="mb-3 rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] p-2">
          <div className={`${LABEL} mb-1.5`}>
            Depth charts · #1 takes first pick of every duty that class does
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {charts.map((cls) => (
              <div key={cls}>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider"
                     style={{ color: tc(classColor(cls)) }}>
                  {cls}
                </div>
                {(depth[cls] || []).map((id, i) => {
                  const r = byId[id];
                  if (!r) return null;
                  return (
                    <div key={id} data-testid={`depth-${cls}-${i}`}
                         className="flex items-center gap-1 py-[1px]">
                      <span className="w-3 shrink-0 text-[10px] text-[var(--c-ink-4)]">{i + 1}</span>
                      <span className="flex-1 truncate text-[12px] font-semibold"
                            style={{ color: tc(classColor(r.className)) }}>
                        {r.name}
                      </span>
                      {canManage && (
                        <button
                          disabled={i === 0}
                          onClick={() => onReorder(cls, i, i - 1)}
                          title="Move up"
                          className="px-1 text-[10px] text-[var(--c-ink-4)] hover:text-[var(--c-ink)] disabled:opacity-0"
                        >
                          ▲
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map((g) => {
          const inGroup = rolesInGroup(roles, g.key);
          if (inGroup.length === 0) return null;
          return (
            <div key={g.key} className="rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] p-2">
              <div className="mb-1 flex items-baseline gap-1.5">
                <span className={LABEL}>{g.label}</span>
                {g.note && <span className="text-[9px] text-[var(--c-ink-4)]">{g.note}</span>}
              </div>
              {inGroup.map((role) => {
                const r = resolved[role.key];
                if (!r) return null;
                const pool = eligibleFor(role);
                if (pool.length === 0 && !r.raider) return null;
                return (
                  <div key={role.key} data-testid={`role-${role.key}`}
                       className="mb-1 flex items-center gap-1.5">
                    <span className="w-[62px] shrink-0 truncate text-right text-[10px] text-[var(--c-ink-4)]"
                          title={role.label}>
                      {role.short}
                    </span>
                    <div className="min-w-0 flex-1">
                      {role.depth ? (
                        // Reordering happens in the chart above; showing a
                        // second control here would let the two disagree. The
                        // "Mage #1" annotation lives in the tooltip because
                        // inline it ate the width the name needed.
                        <Tip as="div"
                             tip={<>Follows the {role.depth.className.toLowerCase()} depth chart:
                                   <b> #{role.depth.index + 1}</b>. Reorder it above.</>}
                             className="truncate rounded border border-transparent px-1.5 py-[3px]
                                        text-[12px] font-semibold"
                             style={{ color: tc(r.raider ? classColor(r.raider.className) : undefined) }}>
                          {r.raider ? r.raider.name : <span className="text-[var(--c-ink-4)]">&mdash;</span>}
                        </Tip>
                      ) : !canManage ? (
                        <div className="truncate px-1.5 py-[3px] text-[12px] font-semibold"
                             style={{ color: tc(r.raider ? classColor(r.raider.className) : undefined) }}>
                          {r.raider ? r.raider.name : <span className="text-[var(--c-ink-4)]">&mdash;</span>}
                        </div>
                      ) : (
                        <RolePicker
                          value={r.raider}
                          roster={pool}
                          placeholder="Auto"
                          tint={r.source === "manual"}
                          onPick={(id) => onRole(role.key, id)}
                        />
                      )}
                    </div>
                    {role.target && role.target.kind === "text" && !canManage && (
                      <span className="w-[86px] shrink-0 truncate text-[11px] text-[var(--c-ink-3)]">
                        {r.target || "—"}
                      </span>
                    )}
                    {role.target && role.target.kind === "text" && canManage && (
                      <select
                        value={r.target || ""}
                        onChange={(e) => onTarget(role.key, e.target.value)}
                        className="w-[86px] shrink-0 rounded border border-[var(--c-line)] bg-[var(--c-surface)]
                                   px-1 py-[3px] text-[11px] text-[var(--c-ink-2)] outline-none
                                   focus:border-amber-400/60"
                      >
                        {(role.target.options || []).map((o) => (
                          <option key={o} value={o} style={optionStyle()}>{o || "Not set"}</option>
                        ))}
                      </select>
                    )}
                    {role.target && role.target.kind === "raider" && !canManage && (
                      <span className="w-[96px] shrink-0 truncate text-[11px] font-semibold"
                            style={{ color: tc(r.targetRaider ? classColor(r.targetRaider.className) : undefined) }}>
                        {r.targetRaider ? r.targetRaider.name : "—"}
                      </span>
                    )}
                    {role.target && role.target.kind === "raider" && canManage && (
                      <div className="w-[96px] shrink-0">
                        <RolePicker
                          value={r.targetRaider}
                          roster={roster}
                          placeholder="Onto"
                          tint={r.targetManual}
                          onPick={(id) => onTarget(role.key, id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────

// Opened from the Planner's Assignments button, always for one known raid.
// Everything else it needs, it derives from that id.
export type AssignmentBoardProps = {
  raidId: string;
  raidName?: string;
  onBack?: () => void;
};

export default function AssignmentBoardPage({
  raidId, raidName, onBack,
}: AssignmentBoardProps) {
  useLegible();
  const [roster, setRoster] = useState<Raider[]>([]);
  // Encounters, night roles and the blessing catalogue all arrive resolved
  // from the server, with this guild's rows layered over the built-in ones.
  const [sheetConfig, setSheetConfig] = useState<SheetConfig>(EMPTY_CONFIG);
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [meCharId, setMeCharId] = useState<string | null>(null);
  const [viewAs, setViewAs] = useState<string>("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleKey | "">("");
  const [bossFilter, setBossFilter] = useState<string>("");
  const [showRoster, setShowRoster] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  // Owner, officer, raid leader or loot master. Resolved server-side by
  // /raids/:id/detail, so a raider cannot grant it to themselves by poking
  // at the client.
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Layer 2: decisions that repeat all night.
  const [roleAssign, setRoleAssign] = useState<RoleAssignments>({});
  const [roleTargets, setRoleTargets] = useState<RoleTargets>({});
  const [depthOrder, setDepthOrder] = useState<DepthOrder>({});
  // Lanes and rows switched off per boss.
  const [disabled, setDisabled] = useState<DisabledMap>({});
  // Boxes a lead authored, keyed by the section they belong to.
  const [custom, setCustom] = useState<CustomLanes>({});
  // Paladin buffs. Stored as the exceptions only: the grid recomputes itself
  // from the roster every time, the same as every other box here.
  const [blessings, setBlessings] = useState<BlessingState>({});
  const [editing, setEditing] = useState<string | null>(null);

  // Who this sheet named when it was written, so a carried-over assignment
  // whose player did not sign can still find a like-for-like stand-in.
  const [snapshot, setSnapshot] = useState<RosterSnapshot>({});
  const [carriedFrom, setCarriedFrom] = useState<string | null>(null);
  const [carried, setCarried] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  // Only a deliberate edit may trigger a save. Keying the autosave off
  // "has loaded" was not enough: hydrating sets roster and state, those are
  // effect dependencies, and the effect then wrote the freshly loaded sheet
  // straight back. Every mutator sets this; loading never does.
  const dirty = useRef(false);
  const touch = useCallback(() => { dirty.current = true; }, []);

  // Roster + boss list for the selected raid
  useEffect(() => {
    if (!raidId) return;
    let dead = false;
    setLoading(true);
    setError(null);
    dirty.current = false;
    setEditing(null);

    Promise.all([
      fetch(`/raids/${encodeURIComponent(raidId)}/detail`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null)),
      fetch("/me", { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`/raids/${encodeURIComponent(raidId)}/sheet`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null),
      // Who can interrupt, who can cleanse, which paladin has Sanctuary. All
      // of it per game_id, so this sheet works for the next game version
      // without a second copy of the class table.
      fetch(`/raids/${encodeURIComponent(raidId)}/sheet-config`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([detail, me, sheet, cfg]) => {
      if (dead) return;
      if (!detail || !detail.raid) {
        setError("Could not load that raid.");
        setLoading(false);
        return;
      }

      setCanManage(detail.canManage === true);

      // Saved decisions, or last week's if this raid has none of its own.
      const st = (sheet && sheet.state) || {};
      setRoleAssign(st.roleAssign || {});
      setRoleTargets(st.roleTargets || {});
      setOverrides(st.overrides || {});
      setDisabled(st.disabled || {});
      setCustom(st.custom || {});
      setBlessings(st.blessings || {});
      setDepthOrder((sheet && sheet.depthOrder) || {});
      setSnapshot((sheet && sheet.rosterSnapshot) || {});
      setCarried(!!(sheet && sheet.carried));
      setCarriedFrom((sheet && sheet.carriedFrom) || null);
      setSaveState(sheet && sheet.savedAt ? "saved" : "idle");

      const rows: any[] = Array.isArray(detail.assignments) ? detail.assignments : [];
      const built: Raider[] = rows.map((a) => {
        const className = canonClass(
          a.character_class_name || a.character_metadata?.class_name || null);
        return {
          characterId: String(a.character_id),
          name: a.character_name || "Unknown",
          className,
          role: normalizeRole(a.role_label, className),
          roleLabel: a.role_label || "",
          userId: a.character_user_id ? String(a.character_user_id) : null,
          // The spec is the key into the game's ability table: interrupts,
          // cleanses and which blessings this paladin actually has.
          specKey: a.character_metadata?.primary_spec || null,
          specLabel: a.character_metadata?.primary_spec_label || null,
          isBench: a.metadata?.is_bench === true
            || a.metadata?.seat_index === undefined
            || a.metadata?.seat_index === null,
          seatIndex: a.metadata?.seat_index != null ? Number(a.metadata.seat_index) : null,
        };
      });
      built.sort((a, b) => {
        const ra = ROLE_ORDER.indexOf(a.role);
        const rb = ROLE_ORDER.indexOf(b.role);
        if (ra !== rb) return ra - rb;
        return a.name.localeCompare(b.name);
      });
      // Fold the game's answer for each raider onto the roster once, here, so
      // every eligibility check downstream is a plain lookup.
      const config: SheetConfig = cfg && cfg.specs ? cfg : EMPTY_CONFIG;
      setSheetConfig(config);
      setRoster(withTraits(built, config));

      const myUserId = me && me.id ? String(me.id) : null;
      const mine = myUserId
        ? built.filter((r) => r.userId === myUserId && !r.isBench)[0]
          || built.filter((r) => r.userId === myUserId)[0]
        : null;
      setMeCharId(mine ? mine.characterId : null);
      setViewAs(mine ? mine.characterId : "");

      // Whole night by default: the pills narrow it, they do not open it.
      setBossFilter("");
      setLoading(false);
    }).catch(() => {
      if (!dead) { setError("Could not load that raid."); setLoading(false); }
    });

    return () => { dead = true; };
  }, [raidId]);

  // Depth chart, then night roles, then the board. Each layer only reads the
  // one below it, so a change to the chart ripples out in one direction.
  const depth = useMemo(
    () => resolveDepth(roster, depthOrder), [roster, depthOrder]);
  const plan = sheetConfig.plan;
  const nightRoles = sheetConfig.nightRoles;
  const catalogue = useMemo(
    () => blessingCatalogue(sheetConfig.blessings), [sheetConfig.blessings]);

  const resolvedRoles = useMemo(
    () => resolveNightRoles(nightRoles, roster, roleAssign, roleTargets, depth, snapshot),
    [nightRoles, roster, roleAssign, roleTargets, depth, snapshot]);

  const planWithCustom = useMemo(
    () => plan.map((p) => {
      const extra = customToLanes(custom[p.key]);
      return extra.length === 0 ? p : { ...p, lanes: p.lanes.concat(extra) };
    }),
    [plan, custom]);

  const board: BossAssignment[] = useMemo(
    () => buildBoard(planWithCustom, roster, overrides, resolvedRoles, disabled, snapshot),
    [planWithCustom, roster, overrides, resolvedRoles, disabled, snapshot]);

  // Stand-ins the carry-over had to pick. A lead should confirm these before
  // the pull rather than discover them in the middle of one.
  // Counted per PERSON, not per slot. A night role binds to a tank lane on
  // every encounter, so one absent main tank used to read as twelve things to
  // confirm on a full Black Temple night. It is one decision, asked once.
  const standIns = useMemo(() => {
    const names: Record<string, boolean> = {};
    board.forEach((b) => b.lanes.forEach((l) => l.slots.forEach((sl) => {
      if (sl.replacedName) names[sl.replacedName] = true;
    })));
    return Object.keys(names).length;
  }, [board]);

  // Paladin buffs are a grid, not a lane list, so they resolve on their own
  // rather than through buildBoard. They still follow the paladin depth chart,
  // which is what makes "promote a paladin" move their whole workload at once.
  const blessingPlan = useMemo(
    () => planBlessings(catalogue, roster, blessings, depth, classOrder(sheetConfig)),
    [catalogue, roster, blessings, depth, sheetConfig]);
  const blessingTouched =
    Object.keys(blessings.grid || {}).length
    + Object.keys(blessings.singles || {}).length
    + Object.keys(blessings.caps || {}).length > 0;

  const load = useMemo(() => loadByRaider(board), [board]);
  const clashes = useMemo(() => clashesFor(board), [board]);
  const viewer = useMemo(
    () => roster.filter((r) => r.characterId === viewAs)[0] || null,
    [roster, viewAs]);
  const pick = useCallback((key: string, characterId: string) => {
    touch();
    setOverrides((prev) => {
      const next: OverrideMap = {};
      Object.keys(prev).forEach((k) => { next[k] = prev[k]; });
      next[key] = characterId;
      return next;
    });
  }, [touch]);


  // Autosave. Debounced because every keystroke in a custom box label is a
  // state change, and a raid lead editing a sheet produces a lot of them.
  const saveTimer = useRef<any>(null);
  useEffect(() => {
    if (!dirty.current || !canManage || !raidId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveState("saving");
      fetch(`/raids/${encodeURIComponent(raidId)}/sheet`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          state: { roleAssign, roleTargets, overrides, disabled, custom, blessings },
          // Written fresh from tonight's roster: this is what NEXT week reads
          // when one of these people does not sign.
          rosterSnapshot: snapshotOf(roster),
          depthOrder,
          carriedFrom,
        }),
      })
        .then((r) => setSaveState(r.ok ? "saved" : "error"))
        .catch(() => setSaveState("error"));
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [raidId, canManage, roleAssign, roleTargets, overrides, disabled, custom,
      blessings, depthOrder, roster, carriedFrom]);

  const hide = useCallback((key: string) => {
    touch();
    setDisabled((prev) => ({ ...prev, [key]: true }));
  }, [touch]);

  // Restoring a lane also clears any rows inside it that were hidden one by
  // one, otherwise the lane comes back missing half of itself.
  const restoreLane = useCallback((bossKey: string, laneKey: string) => {
    touch();
    setDisabled((prev) => {
      const next: DisabledMap = {};
      const prefix = `${bossKey}::${laneKey}`;
      Object.keys(prev).forEach((k) => {
        if (k !== prefix && k.indexOf(`${prefix}::`) !== 0) next[k] = prev[k];
      });
      return next;
    });
  }, [touch]);

  const addBox = useCallback((sectionKey: string) => {
    touch();
    const box = newCustomLane();
    setCustom((prev) => ({ ...prev, [sectionKey]: (prev[sectionKey] || []).concat([box]) }));
    setEditing(box.key);
  }, [touch]);
  const updateBox = useCallback((sectionKey: string, next: CustomLane) => {
    touch();
    setCustom((prev) => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).map((b) => (b.key === next.key ? next : b)),
    }));
  }, [touch]);
  const removeBox = useCallback((sectionKey: string, key: string) => {
    touch();
    setCustom((prev) => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).filter((b) => b.key !== key),
    }));
    setEditing((e) => (e === key ? null : e));
  }, [touch]);

  const setRole = useCallback((roleKey: string, characterId: string) => {
    touch();
    setRoleAssign((prev) => ({ ...prev, [roleKey]: characterId }));
  }, [touch]);
  const setTarget = useCallback((roleKey: string, value: string) => {
    touch();
    setRoleTargets((prev) => ({ ...prev, [roleKey]: value }));
  }, [touch]);
  const reorderDepth = useCallback((className: string, from: number, to: number) => {
    touch();
    setDepthOrder((prev) => {
      const current = (depth[className] || []).slice();
      if (to < 0 || to >= current.length) return prev;
      const moved = current.splice(from, 1)[0];
      current.splice(to, 0, moved);
      return { ...prev, [className]: current };
    });
  }, [depth]);
  // How much of the night sheet has been decided by hand, shown on the toggle
  // so a collapsed panel still says it is holding something.
  const nightSetCount =
    Object.keys(roleAssign).filter((k) => roleAssign[k]).length
    + Object.keys(roleTargets).filter((k) => roleTargets[k]).length
    + Object.keys(depthOrder).length;

  const resetNight = useCallback(() => {
    touch();
    setRoleAssign({});
    setRoleTargets({});
    setDepthOrder({});
  }, [touch]);

  // Blessing grid edits. Same layering as everything else: only the exception
  // is stored, and clearing it hands the cell back to the auto-fill.
  const setBlessingCell = useCallback((palId: string, className: string, value: string) => {
    touch();
    setBlessings((prev) => ({
      ...prev,
      grid: { ...(prev.grid || {}), [cellKey(palId, className)]: value },
    }));
  }, [touch]);
  const setBlessingSingle = useCallback((palId: string, charId: string, value: string) => {
    touch();
    setBlessings((prev) => ({
      ...prev,
      singles: { ...(prev.singles || {}), [singleKey(palId, charId)]: value },
    }));
  }, [touch]);
  const toggleBlessingCap = useCallback((palId: string, b: BlessingKey, on: boolean) => {
    touch();
    setBlessings((prev) => ({
      ...prev,
      caps: { ...(prev.caps || {}), [`${palId}::${b}`]: on ? "1" : "0" },
    }));
  }, [touch]);
  const resetBlessings = useCallback(() => {
    touch();
    setBlessings({});
  }, [touch]);

  const searchLower = search.trim().toLowerCase();

  // Role and search are lead tools: they hunt a single row.
  const slotPasses = useCallback((raider: Raider | null, slot: DutySlot) => {
    if (roleFilter && (!raider || raider.role !== roleFilter)) return false;
    if (searchLower) {
      const inName = raider ? raider.name.toLowerCase().indexOf(searchLower) >= 0 : false;
      const inSlot = slot.label.toLowerCase().indexOf(searchLower) >= 0;
      if (!inName && !inSlot) return false;
    }
    return true;
  }, [roleFilter, searchLower]);

  // "Only my duties" works at LANE level, not row level. A duty means nothing
  // on its own: being 2nd interrupt is useless without knowing who is 1st and
  // 3rd, and healing the main tank is useless without knowing who that is. So
  // the filter keeps whole lanes you appear in, plus any lane that shares one
  // of your raid markers, which is what ties the tank lane to its healers.
  const laneRelevance = useCallback((b: BossAssignment) => {
    const mineLanes: Record<string, boolean> = {};
    const myMarkers: Record<number, boolean> = {};
    b.lanes.forEach((l) => l.slots.forEach((s) => {
      if (s.raider && s.raider.characterId === viewAs) {
        mineLanes[l.lane.key] = true;
        slotMarkers(s.slot).forEach((m) => { myMarkers[m] = true; });
      }
    }));
    return (laneKey: string, slots: { slot: DutySlot }[]) => {
      if (mineLanes[laneKey]) return "mine" as const;
      const linked = slots.some((s) =>
        slotMarkers(s.slot).some((m) => myMarkers[m]));
      return linked ? ("context" as const) : null;
    };
  }, [viewAs]);

  const rowFiltering = !!roleFilter || !!searchLower;
  const filtering = onlyMine || rowFiltering;
  const visibleBosses = board.filter((b) => !bossFilter || b.boss.key === bossFilter);

  // Runs of consecutive bosses sharing a wing, so the label prints once.
  const groupByWing = (list: BossAssignment[]): VisibleGroup[] => {
    const out: VisibleGroup[] = [];
    list.forEach((b) => {
      const label = b.boss.instance || null;
      const tail = out[out.length - 1];
      if (tail && tail.label === label) tail.bosses.push(b);
      else out.push({ label, bosses: [b] });
    });
    return out;
  };

  const bossGroups = useMemo(() => groupByWing(board), [board]);
  const visibleGroups = useMemo(
    () => groupByWing(visibleBosses),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [board, bossFilter]);

  return (
    <div className="mx-auto w-full max-w-[1180px] px-5 pb-20 text-[var(--c-ink)]">

      {/* Header: one line, no hero */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-6 pb-3">
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1 text-[11px]
                       font-semibold text-[var(--c-ink-3)] hover:border-amber-400/40 hover:text-[var(--c-ink)]"
          >
            ← Raid
          </button>
        )}
        <span className="h-5 w-[3px] rounded-full bg-gradient-to-b from-amber-300 to-amber-600" />
        <h1 className="max-w-[340px] truncate text-xl text-[var(--c-ink)]" style={DISPLAY}>
          {raidName || "Raid sheet"}
        </h1>
        <span className="rounded bg-amber-400/10 px-1.5 py-0.5 text-[9px] font-bold uppercase
                         tracking-[0.14em] text-amber-700/80 dark:text-amber-400/80 ring-1 ring-amber-400/20">
          Prototype
        </span>
        {!loading && canManage && saveState !== "idle" && (
          <Tip tip={saveState === "error"
                      ? <>Could not save. Your changes are still on screen but not
                         stored. Check your connection and change something to retry.</>
                      : <>Saved automatically. Officers see the same sheet.</>}
               className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase
                           tracking-[0.14em] ring-1
                           ${saveState === "error"
                             ? "bg-red-500/15 text-red-700 dark:text-red-300 ring-red-500/30"
                             : "bg-[var(--c-sunken)] text-[var(--c-ink-3)] ring-[var(--c-line)]"}`}>
            {saveState === "saving" ? "Saving" : saveState === "error" ? "Not saved" : "Saved"}
          </Tip>
        )}
        {/* Interrupts, cleanses and blessings all resolve through the game's
            ability table. If it did not load, those duties come out empty and
            nothing else would say why. */}
        {!loading && Object.keys(sheetConfig.specs).length === 0 && (
          <Tip tip={<>The sheet could not read this game's ability table, so any duty
                     that needs a specific spell (interrupts, cleanses, blessings) has
                     nobody to offer. Reload, and tell an admin if it persists.</>}
               className="rounded bg-amber-400/10 px-1.5 py-0.5 text-[9px] font-bold
                          uppercase tracking-[0.14em] text-amber-700/90 dark:text-amber-300/90 ring-1 ring-amber-400/20">
            Abilities unavailable
          </Tip>
        )}
        {!loading && carried && (
          <Tip tip={<>Started from your last sheet for this instance. Nothing is
                     stored against this raid until you change something.</>}
               className="rounded bg-[var(--c-sunken)] px-1.5 py-0.5 text-[9px] font-bold
                          uppercase tracking-[0.14em] text-[var(--c-ink-3)] ring-1 ring-[var(--c-line)]">
            Carried over
          </Tip>
        )}
        {!loading && standIns > 0 && (
          <Tip tip={<>{standIns} {standIns === 1 ? "player" : "players"} named on the
                     sheet you carried over {standIns === 1 ? "is" : "are"} not in this
                     raid. The closest match took their duties. Check them.</>}
               className="rounded bg-violet-400/15 px-1.5 py-0.5 text-[9px] font-bold
                          uppercase tracking-[0.14em] text-violet-700 dark:text-violet-300 ring-1
                          ring-violet-400/25">
            {standIns} to confirm
          </Tip>
        )}
        {!loading && !canManage && (
          <Tip tip="Officers, the guild owner, the raid leader and the loot master can edit this sheet."
               className="rounded bg-[var(--c-sunken)] px-1.5 py-0.5 text-[9px] font-bold uppercase
                          tracking-[0.14em] text-[var(--c-ink-3)] ring-1 ring-[var(--c-line)]">
            Read only
          </Tip>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowSetup((v) => !v)}
            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition-colors
                        ${showSetup
                          ? "bg-[var(--c-ink)] text-[var(--c-surface)]"
                          : "border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-3)] hover:border-amber-400/40 hover:text-[var(--c-ink)]"}`}
          >
            Night sheet
            {nightSetCount > 0 && (
              <span className={showSetup ? "ml-1 opacity-60" : "ml-1 text-amber-700 dark:text-amber-400"}>
                {nightSetCount}
              </span>
            )}
          </button>

          <select
            value={viewAs}
            onChange={(e) => setViewAs(e.target.value)}
            className={FIELD}
            style={{ color: tc(viewer ? classColor(viewer.className) : undefined) }}
          >
            <option value="" style={optionStyle()}>Nobody</option>
            {roster.map((r) => (
              <option key={r.characterId} value={r.characterId}
                      style={optStyle(classColor(r.className))}>
                {r.name}{r.characterId === meCharId ? " (you)" : ""} · {ROLE_META[r.role].label}
              </option>
            ))}
          </select>

          <button
            // Hides every row that is not yours, in place. The board keeps its
            // shape; lanes and bosses that end up empty drop out with it.
            onClick={() => setOnlyMine((v) => !v)}
            disabled={!viewAs}
            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-25
                        ${onlyMine
                          ? "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)]"
                          : "border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-3)] hover:border-amber-400/40 hover:text-[var(--c-ink)]"}`}
          >
            Only my duties
          </button>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find"
            className={`${FIELD} w-24 placeholder:text-[var(--c-ink-4)]`}
          />

          <div className="flex items-center gap-0.5">
            {ROLE_ORDER.map((rk) => {
              const on = roleFilter === rk;
              const meta = ROLE_META[rk];
              return (
                <button
                  key={rk}
                  onClick={() => setRoleFilter(on ? "" : rk)}
                  title={meta.label}
                  className="rounded-md border px-1.5 py-1 text-[10px] font-semibold uppercase
                             tracking-wider transition-colors"
                  style={{
                    color: tc(on ? "#0B0D10" : meta.color),
                    background: on ? meta.color : `${meta.color}0F`,
                    borderColor: on ? meta.color : `${meta.color}33`,
                  }}
                >
                  {meta.short}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-800 dark:text-red-200">
          {error}
        </div>
      )}
      {loading && !error && (
        <div className="py-20 text-center text-xs text-[var(--c-ink-4)]">Loading roster</div>
      )}

      {!loading && !error && roster.length === 0 && (
        <div className="py-20 text-center text-sm text-[var(--c-ink-3)]">
          {raidName || "This raid"} has nobody seated yet.
        </div>
      )}

      {!loading && !error && roster.length > 0 && (
        <>
          {showSetup && (
            <NightSetup
              canManage={canManage}
              roster={roster}
              groups={sheetConfig.nightGroups}
              roles={nightRoles}
              resolved={resolvedRoles}
              assignments={roleAssign}
              targets={roleTargets}
              depth={depth}
              depthOrder={depthOrder}
              onRole={setRole}
              onTarget={setTarget}
              onReorder={reorderDepth}
              onReset={resetNight}
            />
          )}

          {/* Boss strip. One row per instance, never wrapped: on a linked
              night each wing has to stay a single readable line, so a long
              wing scrolls sideways instead of folding into the one below. */}
          <div className="mb-4 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1.5">
            {bossGroups.map((g, gi) => (
              <div key={g.label || `_${gi}`}
                   className="flex items-center gap-1 overflow-x-auto whitespace-nowrap
                              [scrollbar-width:thin]">
                {gi === 0 ? (
                  <button
                    onClick={() => setBossFilter("")}
                    className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors
                                ${!bossFilter
                                  ? "bg-[var(--c-ink)] text-[var(--c-surface)]"
                                  : "text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]"}`}
                  >
                    All
                  </button>
                ) : (
                  <span className="w-[34px] shrink-0" />
                )}
                {g.label && (
                  <span className={`${LABEL} w-[130px] shrink-0 truncate border-l border-[var(--c-line)] pl-2.5`}>
                    {g.label}
                  </span>
                )}
                {g.bosses.map((b) => (
                  <button
                    key={b.boss.key}
                    onClick={() => setBossFilter(b.boss.key)}
                    className={`shrink-0 rounded-md px-2 py-1 text-[11px] transition-colors
                                ${bossFilter === b.boss.key
                                  ? "bg-[var(--c-accent)] font-semibold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)]"
                                  : "text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)] hover:text-[var(--c-ink)]"}`}
                  >
                    {b.boss.name}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Board. Wing headings are emitted once per run of bosses, never
              repeated on each section, which is what made the expanded view
              read as an endless "Serpentshrine Cavern, Serpentshrine Cavern". */}
          <div data-testid="board">
          {visibleGroups.map((group, gi) => (
          <div key={group.label || `_${gi}`} className="space-y-5">
            {group.label && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700/70 dark:text-amber-400/70">
                  {group.label}
                </span>
                <span className="h-px flex-1 bg-gradient-to-r from-amber-400/25 to-transparent" />
              </div>
            )}
            {group.bosses.map((b) => {
              const relevance = laneRelevance(b);
              const laneCards = b.lanes.map((l) => ({
                lane: l,
                kept: l.slots.filter((s) => slotPasses(s.raider, s.slot)),
                rel: onlyMine ? relevance(l.lane.key, l.slots) : "mine" as const,
              }))
                .filter((x) => x.rel !== null)
                .filter((x) => !rowFiltering || x.kept.length > 0);

              // General survives an empty filter when the viewer still has
              // blessings to cast or carry, otherwise "only my duties" hides
              // the one box that concerns every single person in the raid.
              const keepForBlessings = b.boss.kind === "general" && onlyMine
                && blessingDutiesFor(blessingPlan, viewAs).length > 0;
              if (filtering && laneCards.length === 0 && !keepForBlessings) return null;

              return (
                <section key={b.boss.key} data-testid={`boss-${b.boss.key}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className={`h-3.5 w-[3px] rounded-full
                                      ${b.boss.kind === "general" ? "bg-[var(--c-ink-3)]" : "bg-amber-400"}`} />
                    <h2 className="text-[15px] font-bold tracking-tight">{b.boss.name}</h2>
                    {b.boss.note && (
                      <span className="truncate text-[11px] text-[var(--c-ink-4)]">{b.boss.note}</span>
                    )}
                    <span className="h-px flex-1 bg-gradient-to-r from-[var(--c-sunken-2)] to-transparent" />
                    {canManage && (
                    <Tip tip="Add your own box: your header, your rows, your markers"><button
                      onClick={() => addBox(b.boss.key)}
                      className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold
                                 uppercase tracking-wider text-[var(--c-ink-4)] hover:text-amber-700 dark:hover:text-amber-300"
                    >
                      + Box
                    </button></Tip>
                    )}
                    {clashes[b.boss.key] > 0 && (
                      <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-400/10
                                       px-2 py-0.5 text-[10px] font-semibold text-amber-700/90 dark:text-amber-300/90">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        {clashes[b.boss.key]} doubled
                      </span>
                    )}
                  </div>

                  {/* Paladin buffs sit in General because they are set once
                      for the night, and full width because a nine-column
                      matrix squeezed into a lane card is unreadable. Under
                      "only my duties" it collapses to the one line that
                      concerns the viewer. */}
                  {b.boss.kind === "general" && (
                    onlyMine
                      ? <MyBlessings plan={blessingPlan} characterId={viewAs} />
                      : (
                        <BlessingBoard
                          plan={blessingPlan}
                          roster={roster}
                          canManage={canManage}
                          touched={blessingTouched}
                          onSetCell={setBlessingCell}
                          onSetSingle={setBlessingSingle}
                          onToggleCap={toggleBlessingCap}
                          onReset={resetBlessings}
                        />
                      )
                  )}

                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                    {(custom[b.boss.key] || []).filter((c) => editing === c.key).map((c) => (
                      <CustomEditor
                        key={c.key}
                        box={c}
                        onChange={(next) => updateBox(b.boss.key, next)}
                        onDone={() => setEditing(null)}
                        onRemove={() => removeBox(b.boss.key, c.key)}
                      />
                    ))}
                    {laneCards.map(({ lane: l, kept, rel }) => {
                      const accent = LANE_ACCENT[l.lane.accent];
                      const shown = rowFiltering ? kept : l.slots;
                      const isCustom = l.lane.key.indexOf("cl_") === 0;
                      if (isCustom && editing === l.lane.key) return null;
                      return (
                        <div key={l.lane.key}
                             className="group/lane overflow-hidden rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)]">
                          <div
                            className="flex items-center gap-1.5 border-b border-[var(--c-line)] px-2 py-[5px]"
                            style={{ background: `linear-gradient(90deg, ${accent}1F, transparent 70%)` }}
                          >
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
                            <span className="text-[10px] font-bold uppercase tracking-[0.14em]"
                                  style={{ color: tc(accent) }}>
                              {l.lane.label}
                            </span>
                            {rel === "context" && (
                              <span title="Shown because it shares your marker"
                                    className="text-[9px] uppercase tracking-wider text-[var(--c-ink-4)]">
                                context
                              </span>
                            )}
                            <span className="ml-auto text-[10px] font-semibold text-[var(--c-ink-4)]">
                              {shown.length}
                            </span>
                            {isCustom && canManage && (
                              <Tip tip="Rename this box or change its rows"><button
                                onClick={() => setEditing(l.lane.key)}
                                className="px-0.5 text-[10px] text-[var(--c-ink-4)] opacity-0
                                           transition-opacity hover:text-[var(--c-ink)]
                                           group-hover/lane:opacity-100"
                              >
                                edit
                              </button></Tip>
                            )}
                            {canManage && (
                            <Tip tip={isCustom
                                ? "Delete this box"
                                : <>Drop <b>{l.lane.label}</b> from {b.boss.name}. It stays on every other boss.</>}
                            ><button
                              onClick={() => isCustom
                                ? removeBox(b.boss.key, l.lane.key)
                                : hide(laneId(b.boss.key, l.lane.key))}
                              data-trim={isCustom ? "box" : "lane"}
                              className="-mr-0.5 px-0.5 text-[11px] leading-none text-[var(--c-ink-4)]
                                         opacity-0 transition-opacity hover:text-[var(--c-ink)]
                                         group-hover/lane:opacity-100"
                            >
                              ✕
                            </button></Tip>
                            )}
                          </div>
                          <div className="p-1">
                          {shown.map((s) => (
                            <SlotRow
                              key={s.slot.key}
                              id={slotId(b.boss.key, l.lane.key, s.slot.key)}
                              slot={s.slot}
                              raider={s.raider}
                              eligible={s.eligible}
                              source={s.source}
                              doubled={!!s.doubled}
                              isMe={!!s.raider && s.raider.characterId === viewAs}
                              targetLabel={targetLabelFor(s)}
                              replacedName={s.replacedName}
                              targetChoices={targetChoicesFor(s.slot, roster)}
                              canManage={canManage}
                              onPick={(cid) =>
                                pick(slotId(b.boss.key, l.lane.key, s.slot.key), cid)}
                              onPickTarget={(v) =>
                                pick(targetId(slotId(b.boss.key, l.lane.key, s.slot.key)), v)}
                              onHide={() =>
                                hide(slotId(b.boss.key, l.lane.key, s.slot.key))}
                            />
                          ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Notes ride in the same grid as a box with no players.
                        A real sheet carries more of these than assignments,
                        and they are what a raider actually reads before a
                        first pull. */}
                    {b.boss.notes && b.boss.notes.length > 0 && !filtering && (
                      <div data-testid={`notes-${b.boss.key}`}
                           className="overflow-hidden rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)]">
                        <div className="flex items-center gap-1.5 border-b border-[var(--c-line)] px-2 py-[5px]"
                             style={{ background: "linear-gradient(90deg, #ffffff14, transparent 70%)" }}>
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--c-ink-4)]" />
                          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--c-ink-3)]">
                            Notes
                          </span>
                          <span className="ml-auto text-[10px] font-semibold text-[var(--c-ink-4)]">
                            {b.boss.notes.length}
                          </span>
                        </div>
                        <ul className="space-y-[3px] p-1.5">
                          {b.boss.notes.map((n, i) => (
                            <li key={i} className="flex gap-1.5 text-[11px] leading-snug text-[var(--c-ink-3)]">
                              <span className="shrink-0 text-[var(--c-ink-4)]">·</span>
                              <span>{n}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* What was switched off here, and the way back. Kept as a
                      quiet footer so a stripped boss still admits what it is
                      missing rather than looking like a data problem. */}
                  {b.hiddenLanes.length > 0 && !filtering && canManage && (
                    <div data-testid={`hidden-${b.boss.key}`}
                         className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className={LABEL}>Not needed here</span>
                      {b.hiddenLanes.map((h) => (
                        <Tip key={h.key} tip={<>Put <b>{h.label}</b> back on {b.boss.name}</>}><button
                          onClick={() => restoreLane(b.boss.key, h.key)}
                          className="rounded border border-[var(--c-line)] px-1.5 py-[1px] text-[10px]
                                     text-[var(--c-ink-4)] hover:border-amber-400/40 hover:text-[var(--c-ink)]"
                        >
                          {h.label} <span className="text-[var(--c-ink-4)]">+</span>
                        </button></Tip>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
          ))}

          {filtering && visibleBosses.every((b) =>
            b.lanes.every((l) => l.slots.every((s) => !slotPasses(s.raider, s.slot)))) && (
            <div className="py-16 text-center text-xs text-[var(--c-ink-4)]">
              {onlyMine ? "You have no named duties tonight."
                        : "Nothing matches those filters."}
            </div>
          )}
          </div>

          {/* Coverage: collapsed by default, it is a lead's tool not a raider's */}
          <div className="mt-8 border-t border-[var(--c-line-soft)] pt-3">
            <button
              onClick={() => setShowRoster((v) => !v)}
              className={`${LABEL} hover:text-[var(--c-ink-2)]`}
            >
              {showRoster ? "Hide" : "Show"} duty load · {roster.length} raiders
            </button>
            {showRoster && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                {roster.map((r) => {
                  const n = load[r.characterId] || 0;
                  return (
                    <button
                      key={r.characterId}
                      onClick={() => setViewAs(r.characterId)}
                      className="flex items-center gap-1 text-[11px] hover:underline"
                    >
                      <span className={r.isBench ? "opacity-40" : ""}
                            style={{ color: tc(classColor(r.className)) }}>
                        {r.name}
                      </span>
                      <span className={n === 0 ? "text-[var(--c-ink-4)]" : "text-[var(--c-ink-3)]"}>{n}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
